import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { PermissionPicker } from '../../../../components/PermissionPicker';
import { Skeleton } from '../../../../components/Skeleton';
import { Modal } from '../../../../components/Modal';
import { ApiError } from '../../../../lib/apiError';
import { rolesApi } from '../rolesApi';
import { usePermissionCatalog } from '../usePermissionCatalog';
import type { RoleListItem } from '../roleTypes';

/**
 * Grants for one role. The catalog comes from the server's code-side registry, so the UI
 * can never offer a permission the application does not actually enforce.
 *
 * The grid itself is the shared `<PermissionPicker>`, unbounded here — the create-user form
 * uses the same component bounded to a single role.
 */
export function RolePermissionsModal({
  role,
  onClose,
  onSaved,
}: {
  role: RoleListItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { catalog, loading: catalogLoading } = usePermissionCatalog(role !== null);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!role) return;

    const controller = new AbortController();
    setLoading(true);
    setConfirmed(false);
    setError(null);

    rolesApi
      .get(role.id, controller.signal)
      .then((detail) => setSelected(detail.permissionKeys))
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        const failure = caught instanceof ApiError ? caught : new ApiError('Could not load permissions.', 0);

      setError(failure);

      /*
        No focus call: this form has one control, the permission grid, and a rejection names
        permission KEYS rather than a field an input stands for. Scrolling to a field that
        does not exist would be worse than the banner alone.
      */
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [role]);

  if (!role) return null;

  async function save() {
    if (!role) return;

    setSaving(true);
    setError(null);

    try {
      await rolesApi.setPermissions(role.id, { permissionKeys: selected });
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save permissions.', 0);

      /*
        No focusValidationError here: this form has one control, the permission grid, and a
        rejection names permission KEYS rather than a field an input stands for. Scrolling to
        a field that does not exist would be worse than the banner on its own.
      */
      setError(failure);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Permissions — ${role.name}`}
      size="xl"
      id="role-permissions-modal"
      /*
        Grants every key in the catalog — the one honest generated answer.

        A random subset would be test data that looks like a considered access policy, and the
        next reader could not tell which. "Everything" is unmistakably a fixture, and it is
        also the state that exercises the screens behind the role, which is what the button is
        for. The confirm tick still stands between it and the server.
      */
      autogenerate={{
        // The catalog is grouped by module, so the keys are one level down.
        onGenerate: () =>
          setSelected(catalog.flatMap((module) => module.permissions.map((p) => p.key))),
        disabled: saving || catalog.length === 0,
      }}
      confirm={{
        id: 'role-permissions-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving || loading,
      }}
      footer={
        <>
          <Button id="role-permissions-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="role-permissions-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!confirmed || loading}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
            {error.message}
          </p>
        ) : null}

        {loading || catalogLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <PermissionPicker
            idPrefix="role-perm"
            catalog={catalog}
            value={selected}
            onChange={setSelected}
            disabled={saving}
            emptyMessage="No permissions are registered yet."
          />
        )}
      </div>
    </Modal>
  );
}
